import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  SwipeableDrawer,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { SignRequest } from "models/interfaces";
import { signer as signerTokens } from "theme/colors";
import editorTokens from "screens/SignRequest/SignRequestEditor.tokens";
import {
  AssistantTabIcon,
  CloseIcon,
  InsightsTabIcon,
} from "screens/SignRequest/components/EditorIcons";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AIInsightsPanel from "screens/SignRequest/components/AIInsightsPanel";
import ChatWithPdf from "screens/SignRequest/components/ChatWithPdf";
import ViewDocuments from "./ViewDocuments";
import { streamContractBatchStatus } from "screens/ContractSearch/ContractStatusStream";

/**
 * Post-sign review: the signed PDF on the left, Chat and Summary on the right.
 *
 * The same two-column shape as step 3 of the editor, minus the parts that only make sense while a
 * request is being built. Both panels are the editor's own, handed `signed` so they ask the AI
 * about the SIGNED copy.
 */

type ReviewPanel = "chat" | "insights";

/** The two full-width controls that open the mobile sheet. */
const sheetButtonSx = {
  minHeight: 52,
  borderRadius: 0,
  textTransform: "none" as const,
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "signer.text.meta",
  "&:hover": { backgroundColor: "signer.surface.subtle" },
};

const RAIL_WIDTH = 52;
const PANEL_WIDTH = 380;

/**
 * Whether the signed documents are embedded yet. The AI ingests them in the background once a
 * request completes, so for a few seconds they exist but cannot be answered from.
 *
 * Progress arrives pushed down one connection, not polled: the spinner clears the moment embedding
 * finishes. Requests signed before batch ids were recorded have no `aiBatchId` and count as ready.
 */
const usePreparedDocuments = (batchId: string | undefined, open: boolean) => {
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (!open || !batchId) {
      setPreparing(false);
      return;
    }
    let cancelled = false;

    const close = streamContractBatchStatus(batchId, {
      onProgress: (status) => {
        if (cancelled) return;
        // `done` is the right gate, not `completed`: it means every document is embedded, which is
        // all chat and summary need. Field extraction carries on afterwards.
        setPreparing(!status?.done);
      },
      onDone: () => {
        if (!cancelled) setPreparing(false);
      },
      onFailed: () => {
        // Aged out, or the stream is unavailable. Either way, never trap the user behind a spinner.
        if (!cancelled) setPreparing(false);
      },
    });

    return () => {
      cancelled = true;
      close();
    };
  }, [batchId, open]);

  return preparing;
};

const PostSignReview = ({
  open,
  onClose,
  signRequest,
  initialDocIndex = 0,
}: {
  open: boolean;
  onClose: () => void;
  signRequest: SignRequest;
  initialDocIndex?: number;
}) => {
  const theme = useTheme();
  // Below md the panels move into a bottom sheet. Chosen in JS rather than `display: none` so only
  // one ever mounts -- a hidden ChatWithPdf would still hold its own state.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [panel, setPanel] = useState<ReviewPanel>("insights");
  const [sheetOpen, setSheetOpen] = useState(false);
  // Every file of a request goes up in one batch, so the first id found covers them all.
  const batchId = (signRequest?.files ?? []).find(
    (file: any) => file?.aiBatchId,
  )?.aiBatchId;
  const preparing = usePreparedDocuments(batchId, open);

  const railButton = (
    key: ReviewPanel,
    label: string,
    icon: React.ReactNode,
  ) => {
    const active = panel === key;
    return (
      <Tooltip title={label} arrow placement="left">
        <IconButton
          aria-label={label}
          aria-pressed={active}
          onClick={() => setPanel(key)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            color: active ? "#ffffff" : "signer.text.meta",
            backgroundColor: active ? "signer.orange.main" : "transparent",
            "&:hover": {
              backgroundColor: active
                ? "signer.orange.main"
                : "signer.surface.subtle",
            },
          }}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  };

  // The one definition of what the right-hand side shows, used by the desktop column and by the
  // mobile sheet, so the two can never drift apart.
  const panelContent = preparing ? (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{ height: "100%", px: 4, textAlign: "center" }}
    >
      <CircularProgress size={28} />
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "signer.text.strong",
        }}
      >
        Preparing your document
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "signer.text.meta" }}>
        The signed document is being read so it can be summarised and answered
        on. This usually takes under a minute.
      </Typography>
    </Stack>
  ) : panel === "chat" ? (
    <ChatWithPdf
      signed
      fillHeight
      signRequest={signRequest}
      starterPrompts={[
        "Who signed this document and when?",
        "Were any fields left unsigned?",
        "What is the governing law?",
        "What are the key dates and payment terms?",
      ]}
    />
  ) : (
    <AIInsightsPanel signed signRequest={signRequest} />
  );

  const openSheet = (next: ReviewPanel) => {
    setPanel(next);
    setSheetOpen(true);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      aria-label="Document review"
      PaperProps={{ sx: { backgroundColor: "#f5f6f8" } }}
    >
      <Stack sx={{ height: "100%", minHeight: 0 }}>
        {/* Title strip: which request is open, and the way out. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: "#ffffff",
            borderBottom: `1px solid ${alpha(signerTokens.ink, 0.08)}`,
            flexShrink: 0,
          }}
        >
          {/* The app's existing back control, as used on the renewable documents list: a plain
              tooltipped icon button with the page title beside it. Deliberately not a styled
              button -- back is navigation, not an action, and nothing else in the app gives it
              weight. */}
          <Tooltip title="Go back">
            <IconButton
              aria-label="Go back"
              onClick={onClose}
              sx={{ flexShrink: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="div"
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "signer.text.strong",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {signRequest?.emailSubject || "Signed document"}
            </Typography>
            {/* <Typography
              component="div"
              sx={{
                fontSize: "0.75rem",
                color: "signer.text.meta",
                // Vertical space is scarce on a phone and the tab bar already says this.
                display: { xs: "none", md: "block" },
              }}
            >
              Chat with the signed document or read its summary
            </Typography> */}
          </Box>
        </Stack>

        <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
          {/* Left: the signed PDF, the same viewer the Preview modal uses. */}
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <ViewDocuments
              request={signRequest}
              initialDocIndex={initialDocIndex}
              fillHeight
              hideToolbar
            />
          </Box>

          {/* Right: one panel at a time, chosen from the rail. Desktop only. */}
          {!isCompact && (
            <>
              <Box
                sx={{
                  width: PANEL_WIDTH,
                  flexShrink: 0,
                  minHeight: 0,
                  backgroundColor: "#ffffff",
                  borderLeft: `1px solid ${alpha(signerTokens.ink, 0.08)}`,
                }}
              >
                {panelContent}
              </Box>

              <Stack
                alignItems="center"
                spacing={1}
                sx={{
                  width: RAIL_WIDTH,
                  flexShrink: 0,
                  py: 1.5,
                  backgroundColor: "#ffffff",
                  borderLeft: `1px solid ${alpha(signerTokens.ink, 0.08)}`,
                }}
              >
                {railButton(
                  "chat",
                  "AI Assistant",
                  <AssistantTabIcon
                    size={editorTokens.rightRail.headerIconSize}
                  />,
                )}
                {railButton(
                  "insights",
                  "AI Insights",
                  <InsightsTabIcon
                    size={editorTokens.rightRail.headerIconSize}
                  />,
                )}
              </Stack>
            </>
          )}
        </Stack>

        {/* Mobile: the rail becomes a tab bar under the document, opening a bottom sheet --
            the same shape the editor already uses on small screens (DocumentTabMobile). */}
        {isCompact && (
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: "#ffffff",
              borderTop: `1px solid ${alpha(signerTokens.ink, 0.08)}`,
              // Clear of the phone's home indicator.
              pb: "env(safe-area-inset-bottom)",
            }}
          >
            {/* Plain buttons, not Tabs: nothing here is ever "selected" -- each one opens a sheet --
                so a tab bar held at value={false} was only borrowing MUI's click plumbing for a
                control it does not model. */}
            <Stack direction="row" sx={{ minHeight: 52 }}>
              <Button
                fullWidth
                onClick={() => openSheet("chat")}
                startIcon={<AssistantTabIcon size={18} />}
                sx={sheetButtonSx}
              >
                Chat
              </Button>
              <Button
                fullWidth
                onClick={() => openSheet("insights")}
                startIcon={<InsightsTabIcon size={18} />}
                sx={sheetButtonSx}
              >
                Summary
              </Button>
            </Stack>
          </Box>
        )}

        {isCompact && (
          <SwipeableDrawer
            anchor="bottom"
            open={sheetOpen}
            onOpen={() => setSheetOpen(true)}
            onClose={() => setSheetOpen(false)}
            disableSwipeToOpen
            // Kept mounted so a half-typed question and the answers already on screen survive
            // closing the sheet to look at the document.
            ModalProps={{ keepMounted: true }}
            sx={{
              // This screen IS a full-screen Dialog, and a Dialog and a Drawer both sit at
              // theme.zIndex.modal by default. Without this the sheet opens BEHIND the dialog and
              // tapping the buttons looks like it does nothing at all.
              zIndex: (theme) => theme.zIndex.modal + 1,
              "& .MuiDrawer-paper": {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                height: "90vh",
                overflow: "hidden",
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: alpha(signerTokens.ink, 0.2),
                }}
              />
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ px: 1.5, pt: 0.5, flexShrink: 0 }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "signer.text.strong",
                }}
              >
                {panel === "chat" ? "AI Assistant" : "AI Insights"}
              </Typography>
              <IconButton
                aria-label="Close"
                onClick={() => setSheetOpen(false)}
                disableRipple
              >
                <CloseIcon size={18} />
              </IconButton>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              {panelContent}
            </Box>
          </SwipeableDrawer>
        )}
      </Stack>
    </Dialog>
  );
};

export default PostSignReview;
